<template>
    <div v-if="currentStep === 2" class="mb-2 md:mb-5 h-full flex flex-col">
        <header class="flex justify-between items-center pb-2">
            <h2 class="text-slate-600 dark:text-slate-300 text-sm font-medium font-['Inter'] leading-tight">
                Result
            </h2>
        </header>
        <TransitionGroup tag="ul" name="list" class="details-list">
            <li
                v-for="detail in copyableDetails"
                :key="`receiving-${detail.key}`"
                class="border-slate-200 dark:border-slate-700 border-b transition-all duration-300 ease-in-out"
            >
                <div class="h-14 py-2">
                    <div
                        v-if="isSelected(detail.key)"
                        class="flex items-center gap-3 h-full transition-opacity duration-300"
                    >
                        <div class="flex-1 flex flex-col justify-center">
                            <span class="text-slate-500 dark:text-slate-400 text-sm font-normal font-['Inter'] leading-tight">
                                {{ detail.label }}
                            </span>
                            <div class="flex items-center gap-1">
                                <span
                                    v-if="detail.key === 'annual_fee' && !selectedReceivingCard.user_managed_annual_fee"
                                    class="line-through text-slate-800 dark:text-slate-100 text-sm font-normal font-['Inter'] leading-tight"
                                >
                                    {{ formatCardDetails(selectedReceivingCard.card.annual_fee, detail.key) }}
                                </span>
                                <span
                                    v-else-if="detail.key !== 'welcome_bonus'"
                                    class="line-through text-slate-800 dark:text-slate-100 text-sm font-normal font-['Inter'] leading-tight"
                                >
                                    <p v-if="detail.key !== 'bonus_offers'">
                                        {{ formatCardDetails(selectedReceivingCard[detail.key], detail.key) }}
                                    </p>
                                    <p v-else>{{ selectedReceivingCard.bonus_offers?.length ? '' : 'None' }}</p>
                                </span>
                                <svg
                                    v-show="detail.key !== 'welcome_bonus' && detail.key !== 'bonus_offers'"
                                    width="16" height="16" viewBox="0 0 16 16" fill="none"
                                    xmlns="http://www.w3.org/2000/svg" class="flex-shrink-0"
                                >
                                    <path d="M2.66675 8.00008H13.3334M13.3334 8.00008L9.33341 4.00008M13.3334 8.00008L9.33341 12.0001" stroke="#64748B" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round" />
                                </svg>
                                <span
                                    class="text-slate-800 dark:text-slate-100 text-sm font-normal font-['Inter'] leading-tight"
                                    v-if="detail.key === 'welcome_bonus'"
                                >
                                    <p>{{ selectedCard?.welcome_bonus ? '' : 'None' }}</p>
                                </span>
                                <span
                                    class="text-slate-800 dark:text-slate-100 text-sm font-normal font-['Inter'] leading-tight"
                                    v-if="detail.key !== 'welcome_bonus'"
                                >
                                    <p v-if="detail.key !== 'bonus_offers'">
                                        {{ formatCardDetails(selectedCard[detail.key], detail.key) }}
                                    </p>
                                    <p v-else>{{ selectedCard?.bonus_offers?.length ? '' : 'None' }}</p>
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
                <!-- Welcome bonus sub-details -->
                <Transition name="expand">
                    <template v-if="detail.key === 'welcome_bonus' && selectedDetails[detail.key]">
                        <ul
                            v-if="selectedCard?.welcome_bonus && selectedCard?.welcome_bonus?.type !== 'tiered'"
                            class="pb-4 -mt-1 -ml-1.5 pl-3 transition-all duration-300 ease-in-out"
                        >
                            <li
                                v-for="(subDetail, i) in welcomeBonusDetails"
                                :key="subDetail.key"
                                class="relative flex flex-col gap-1 pt-2 border-l pl-4 h-[52px] transition-all duration-500"
                                :class="i < welcomeBonusDetails.length - 1 ? 'border-slate-300 dark:border-slate-600' : 'border-transparent'"
                            >
                                <SubDetailConnector :index="i" :total="welcomeBonusDetails.length" />
                                <div class="text-slate-600 dark:text-slate-300 text-sm font-normal">{{ subDetail.label }}</div>
                                <div class="flex items-center gap-1">
                                    <span class="line-through text-slate-800 dark:text-slate-100 text-sm font-normal font-['Inter'] leading-tight">
                                        {{ formatCardDetails(selectedReceivingCard.welcome_bonus?.[subDetail.key], subDetail.key) }}
                                    </span>
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" class="flex-shrink-0">
                                        <path d="M2.66675 8.00008H13.3334M13.3334 8.00008L9.33341 4.00008M13.3334 8.00008L9.33341 12.0001" stroke="#64748B" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round" />
                                    </svg>
                                    <div class="text-slate-800 dark:text-slate-100 text-sm font-normal font-['Inter'] leading-tight">
                                        {{ formatCardDetails(selectedCard.welcome_bonus?.[subDetail.key], subDetail.key) }}
                                    </div>
                                </div>
                            </li>
                        </ul>
                        <ul
                            v-else-if="selectedCard?.welcome_bonus?.type === 'tiered'"
                            class="pb-4 -mt-1 -ml-1.5 pl-3"
                        >
                            <li
                                v-for="(bonusTier, i) in selectedCard.welcome_bonus?.tiers"
                                :key="bonusTier.id"
                                class="relative flex justify-between pt-2 border-l pl-4 h-[52px]"
                                :class="i < selectedCard.welcome_bonus.tiers.length - 1 ? 'border-slate-300 dark:border-slate-600' : 'border-transparent'"
                            >
                                <SubDetailConnector :index="i" :total="selectedCard.welcome_bonus.tiers.length" />
                                <div class="flex flex-col gap-[2px]">
                                    <span class="text-slate-600 dark:text-slate-300 text-sm font-normal">{{ `Tier ${i + 1}` }}</span>
                                    <span class="text-slate-800 dark:text-slate-100 text-sm font-medium">{{ formatBonusOfferDisplay(bonusTier) }}</span>
                                </div>
                                <BonusReceivedBadge :item="bonusTier" />
                            </li>
                        </ul>
                    </template>
                </Transition>
                <!-- Bonus offers sub-details -->
                <Transition name="expand">
                    <ul
                        v-if="detail.key === 'bonus_offers' && selectedDetails[detail.key] && selectedCard?.bonus_offers?.length > 0"
                        class="pb-4 -mt-1 -ml-1.5 pl-3"
                    >
                        <li
                            v-for="(bonusOffer, i) in selectedCard.bonus_offers"
                            :key="bonusOffer.id"
                            class="relative flex justify-between pt-2 border-l pl-4 h-[52px]"
                            :class="i < selectedCard.bonus_offers.length - 1 ? 'border-slate-300 dark:border-slate-600' : 'border-transparent'"
                        >
                            <SubDetailConnector :index="i" :total="selectedCard.bonus_offers.length" />
                            <div class="flex flex-col gap-[2px]">
                                <span class="text-slate-600 dark:text-slate-300 text-sm font-normal">{{ $filters.titleCase(bonusOffer.type) }} offer</span>
                                <span class="text-slate-800 dark:text-slate-100 text-sm font-medium">{{ formatBonusOfferDisplay(bonusOffer) }}</span>
                            </div>
                            <BonusReceivedBadge :item="bonusOffer" />
                        </li>
                    </ul>
                </Transition>
                <!-- Product changed sub-details -->
                <Transition name="expand">
                    <ul
                        v-if="detail.key === 'product_changed' && selectedDetails[detail.key] && selectedCard?.product_changed"
                        class="pb-4 -mt-1 -ml-1.5 pl-3 transition-all duration-300 ease-in-out"
                    >
                        <li
                            v-for="(subDetail, i) in productChangedDetails"
                            :key="subDetail.key"
                            class="relative flex flex-col gap-1 pt-2 border-l pl-4 h-[52px] transition-all duration-300 ease-in-out"
                            :class="i < productChangedDetails.length - 1 ? 'border-slate-300 dark:border-slate-600' : 'border-transparent'"
                        >
                            <SubDetailConnector :index="i" :total="productChangedDetails.length" />
                            <div class="text-slate-600 dark:text-slate-300 text-sm font-normal">{{ subDetail.label }}</div>
                            <div class="flex items-center gap-1">
                                <div class="text-slate-800 dark:text-slate-100 text-sm font-normal font-['Inter'] leading-tight whitespace-nowrap truncate block"
                                    :title="subDetail.key === 'original_card' ? selectedCard?.original_card?.title : formatCardDetails(selectedCard[subDetail.key], subDetail.key)"
                                >
                                    {{ subDetail.key === 'original_card' ? selectedCard?.original_card?.title : formatCardDetails(selectedCard[subDetail.key], subDetail.key) }}
                                </div>
                            </div>
                        </li>
                    </ul>
                </Transition>
            </li>
        </TransitionGroup>
    </div>
</template>

<script setup>
import SubDetailConnector from './SubDetailConnector.vue';
import BonusReceivedBadge from './BonusReceivedBadge.vue';

defineProps({
    currentStep: { type: Number, required: true },
    selectedCard: { type: Object, default: null },
    selectedReceivingCard: { type: Object, default: null },
    selectedDetails: { type: Object, required: true },
    copyableDetails: { type: Array, required: true },
    welcomeBonusDetails: { type: Array, required: true },
    productChangedDetails: { type: Array, required: true },
    isSelected: { type: Function, required: true },
    formatCardDetails: { type: Function, required: true },
    formatBonusOfferDisplay: { type: Function, required: true },
});
</script>
